import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { api, apiError } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { isDeletionBlocking } from '@/components/account/DeletionPanel';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';

function Check({ checked, onChange, testId, children }) {
  return (
    <label className="flex cursor-pointer items-start gap-3 text-sm text-white">
      <Checkbox checked={checked} onCheckedChange={(v) => onChange(v === true)} className="mt-0.5" data-testid={testId} />
      <span>{children}</span>
    </label>
  );
}

// Contractual acceptance only (CGU + privacy). Optional AI-training consent must stay separate.
export function TermsAcceptanceModal() {
  const { session, profile, signOut } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [cgu, setCgu] = useState(false);
  const [privacy, setPrivacy] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!session || !profile || isDeletionBlocking(profile.deletion)) { setStatus(null); return; }
    api.get('/legal/status').then(({ data }) => setStatus(data)).catch(() => setStatus(null));
  }, [session, profile]);

  if (!status?.required) return null;

  const accept = async () => {
    setBusy(true);
    try {
      await api.post('/legal/accept', { terms_version: status.terms_version, privacy_version: status.privacy_version, source: 'web' });
      setStatus({ ...status, required: false });
      toast.success('Merci, bonne création !');
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const refuse = async () => { await signOut(); navigate('/login', { replace: true }); };

  const block = (e) => e.preventDefault();
  return (
    <Dialog open>
      <DialogContent className="glass w-[calc(100%-2rem)] max-w-lg rounded-2xl [&>button]:hidden" onInteractOutside={block} onEscapeKeyDown={block} data-testid="terms-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Bienvenue sur MyMusic IA</DialogTitle>
          <DialogDescription className="text-white/80">
            Pour continuer, veuillez prendre connaissance de nos Conditions générales d'utilisation et de notre Politique de confidentialité.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-wrap gap-x-5 gap-y-1 text-sm">
          <Link to="/terms" target="_blank" className="text-brand hover:underline" data-testid="terms-modal-terms-link">Conditions générales d'utilisation</Link>
          <Link to="/privacy-policy" target="_blank" className="text-brand hover:underline" data-testid="terms-modal-privacy-link">Politique de confidentialité</Link>
        </div>
        <div className="space-y-3">
          <Check checked={cgu} onChange={setCgu} testId="terms-modal-checkbox-cgu">J'ai lu et j'accepte les Conditions générales d'utilisation de MyMusic IA.</Check>
          <Check checked={privacy} onChange={setPrivacy} testId="terms-modal-checkbox-privacy">Je reconnais avoir pris connaissance de la Politique de confidentialité.</Check>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row-reverse">
          <button onClick={accept} disabled={!cgu || !privacy || busy} data-testid="terms-modal-continue"
                  className="flex flex-1 items-center justify-center rounded-full bg-brand py-3 font-semibold text-black btn-glow disabled:opacity-50">
            {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : 'Accepter et continuer'}
          </button>
          <button onClick={refuse} data-testid="terms-modal-refuse"
                  className="flex-1 rounded-full border border-border py-3 font-semibold text-white hover:bg-white/5">
            Refuser et quitter
          </button>
        </div>
        <p className="text-center text-xs text-muted-foreground">
          Refuser ne supprime pas ton compte. <Link to="/delete-account" className="hover:text-brand" data-testid="terms-modal-delete-link">Gérer mes données / supprimer mon compte</Link>
        </p>
      </DialogContent>
    </Dialog>
  );
}
